import { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import {
  Search, MapPin, Briefcase, Clock, ChevronRight,
  X, Send, Mail, Phone, CheckCircle2,
  Building2, ArrowRight, Filter, Users, Award, Zap
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

/* ─── Google Fonts (only font import, no custom CSS classes) ─────────── */
const FontLink = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,600&family=Outfit:wght@300;400;500;600;700&display=swap');
  `}</style>
);

/* ─── Job Data ──────────────────────────────────────────────────────────── */
const JOBS = [
  {
    id: 1,
    title: 'SERVICE EXECUTIVE',
    dept: 'SERVICE',
    location: 'Hyderabad',
    type: 'SERVICE',
    posted: '4 years ago',
    desc: 'Responsible for installation, servicing, and maintenance of laboratory instruments at client sites. Requires hands-on technical skills and excellent client communication.',
    skills: ['Instrument Servicing', 'Field Support', 'Documentation', 'Client Handling'],
    openings: 2,
  },
  {
    id: 2,
    title: 'SERVICE EXECUTIVE / SR. SERVICE EXECUTIVE',
    dept: 'SERVICE',
    location: 'Bangalore, Chennai',
    type: 'SERVICE',
    posted: '4 years ago',
    desc: 'Installation, commissioning, and preventive maintenance of scientific laboratory instruments. Provide technical training and support to end users.',
    skills: ['Commissioning', 'Preventive Maintenance', 'Training', 'Troubleshooting'],
    openings: 3,
  },
  {
    id: 3,
    title: 'SERVICE EXECUTIVE / SERVICE ENGINEER / SR. SERVICE ENGINEER',
    dept: 'SERVICE',
    location: 'Hyderabad',
    type: 'SERVICE',
    posted: '4 years ago',
    desc: 'Multi-level service role covering installation to senior engineering responsibilities. Ideal for candidates with experience in scientific or medical equipment service.',
    skills: ['Service Engineering', 'AMC Management', 'Calibration', 'NABL Standards'],
    openings: 2,
  },
  {
    id: 4,
    title: 'SALES SUPPORT EXECUTIVE',
    dept: 'SALES SUPPORT',
    location: 'Hyderabad',
    type: 'SALES SUPPORT',
    posted: '4 years ago',
    desc: 'Coordinate between the sales team and clients to ensure smooth order processing, documentation, and timely delivery of laboratory equipment and instruments.',
    skills: ['Order Processing', 'CRM', 'Coordination', 'MS Office'],
    openings: 1,
  },
  {
    id: 5,
    title: 'SALES MANAGER',
    dept: 'SALES MANAGER',
    location: 'Hyderabad',
    type: 'SALES MANAGER',
    posted: '4 years ago',
    desc: 'Lead and manage the regional sales team to achieve targets in the laboratory and scientific instruments market. Build relationships with pharma, research, and academic clients.',
    skills: ['Team Leadership', 'B2B Sales', 'Target Achievement', 'Key Accounts'],
    openings: 1,
  },
  {
    id: 6,
    title: 'SALES EXECUTIVE',
    dept: 'SALES EXECUTIVE',
    location: 'Hyderabad',
    type: 'SALES EXECUTIVE',
    posted: '4 years ago',
    desc: 'Drive sales of laboratory instruments and equipment to research institutions, pharmaceutical companies, hospitals, and universities across the assigned territory.',
    skills: ['Territory Sales', 'Lead Generation', 'Product Demo', 'Negotiation'],
    openings: 2,
  },
];

const JOB_TYPES = ['All', 'SALES EXECUTIVE', 'SALES MANAGER', 'SALES SUPPORT', 'SERVICE'];
const LOCATIONS = ['All', 'Hyderabad', 'Bangalore', 'Chennai'];

/* ─── Colour map for dept badges ─────────────────────────── */
const TYPE_COLOR = {
  'SERVICE': { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', dot: 'bg-sky-500' },
  'SALES SUPPORT': { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', dot: 'bg-violet-500' },
  'SALES MANAGER': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  'SALES EXECUTIVE': { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', dot: 'bg-teal-500' },
};

/* ─── Reveal wrapper ─────────────────────────────────────── */
function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Apply Modal ────────────────────────────────────────── */
function ApplyModal({ job, onClose }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', city: '', message: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const colors = TYPE_COLOR[job.type] || TYPE_COLOR['SERVICE'];

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!/^\d{10}$/.test(form.phone.replace(/\s/g, ''))) e.phone = 'Valid 10-digit number';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required';
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    setTimeout(() => { setLoading(false); setDone(true); }, 1300);
  };

  return (
    <div className="flex flex-col h-full max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl">
      {/* Modal Header - Premium gradient with hero overlay style */}
      <div className="relative flex-shrink-0 px-6 py-5 overflow-hidden bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)]">
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
        <div className="flex items-start justify-between relative z-10 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
              <p className="text-[11px] font-bold text-sky-200 tracking-widest uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {job.type.replace('_', ' ')} · Apply Now
              </p>
            </div>
            <h3 className="text-xl font-bold text-white leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>{job.title}</h3>
            <div className="flex items-center gap-3 mt-2">
              <span className="flex items-center gap-1 text-xs text-white/70" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <MapPin size={11} /> {job.location}
              </span>
              <span className="flex items-center gap-1 text-xs text-white/70" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <Briefcase size={11} /> {job.dept}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-white/70 hover:bg-white/20 hover:text-white transition-all flex-shrink-0"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      {/* Modal Body - Premium input styling */}
      <div className="flex-1 overflow-y-auto bg-white">
        <AnimatePresence mode="wait">
          {done ? (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center p-10 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4"
              >
                <CheckCircle2 size={32} className="text-emerald-500" />
              </motion.div>
              <h4 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Application Submitted!</h4>
              <p className="text-sm text-slate-500 leading-relaxed max-w-xs" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Thank you, <strong className="text-blue-600">{form.name}</strong>. Our HR team will review your application and reach out within 3–5 business days.
              </p>
              <button
                onClick={onClose}
                className="mt-6 px-6 py-2.5 rounded-xl text-sm font-semibold text-white bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)] hover:shadow-lg transition-all"
              >
                Close
              </button>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onSubmit={handleSubmit}
              className="flex flex-col gap-3.5 p-6"
            >
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Outfit', sans-serif" }}>Full Name *</label>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={form.name}
                    onChange={handleChange('name')}
                    className={`w-full px-3 py-2.5 text-sm bg-slate-50 border rounded-xl outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all ${errors.name ? 'border-red-400' : 'border-slate-200'}`}
                  />
                  {errors.name && <p className="text-[10px] text-red-400 mt-0.5">{errors.name}</p>}
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Outfit', sans-serif" }}>Phone *</label>
                  <input
                    type="tel"
                    placeholder="10-digit number"
                    value={form.phone}
                    onChange={handleChange('phone')}
                    className={`w-full px-3 py-2.5 text-sm bg-slate-50 border rounded-xl outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all ${errors.phone ? 'border-red-400' : 'border-slate-200'}`}
                  />
                  {errors.phone && <p className="text-[10px] text-red-400 mt-0.5">{errors.phone}</p>}
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Outfit', sans-serif" }}>Email Address *</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={form.email}
                  onChange={handleChange('email')}
                  className={`w-full px-3 py-2.5 text-sm bg-slate-50 border rounded-xl outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all ${errors.email ? 'border-red-400' : 'border-slate-200'}`}
                />
                {errors.email && <p className="text-[10px] text-red-400 mt-0.5">{errors.email}</p>}
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Outfit', sans-serif" }}>Current City</label>
                <input
                  type="text"
                  placeholder="City you're based in"
                  value={form.city}
                  onChange={handleChange('city')}
                  className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Outfit', sans-serif" }}>Cover Note (Optional)</label>
                <textarea
                  rows={3}
                  placeholder="Tell us about your experience and why you're a great fit..."
                  value={form.message}
                  onChange={handleChange('message')}
                  className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all resize-none"
                />
              </div>

              {/* Premium button with shine effect and hero overlay style */}
              <button
                type="submit"
                disabled={loading}
                className="relative overflow-hidden flex items-center justify-center gap-2 py-3.5 rounded-xl text-sm font-bold text-white bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-white/20 before:to-transparent before:transition-transform before:duration-500 hover:before:translate-x-0"
              >
                {loading ? (
                  <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                ) : (
                  <>
                    <Send size={14} /> Submit Application
                  </>
                )}
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ─── Job Card ───────────────────────────────────────────── */
function JobCard({ job, index, onApply }) {
  const [expanded, setExpanded] = useState(false);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const colors = TYPE_COLOR[job.type] || TYPE_COLOR['SERVICE'];

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay: index * 0.07, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="bg-white border border-slate-100 rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:border-blue-200 hover:-translate-y-1 group"
    >
      {/* Top accent line - Hero overlay style */}
      <div className="h-1 w-full bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)]" />

      <div className="p-5 sm:p-6">
        {/* Header row */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            {/* Dept badge */}
            <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full border mb-2.5 ${colors.bg} ${colors.text} ${colors.border}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
              <Briefcase size={9} />
              {job.dept}
            </span>
            <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-snug group-hover:text-blue-700 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
              {job.title}
            </h3>
          </div>
          {/* Openings badge - Hero overlay style */}
          <div className="flex-shrink-0 text-center bg-[linear-gradient(135deg,rgba(15,35,86,0.08)_0%,rgba(30,58,138,0.06)_50%,rgba(14,165,233,0.08)_100%)] border border-blue-100 rounded-xl px-3 py-2">
            <p className="text-xl font-bold text-blue-700 leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>{job.openings}</p>
            <p className="text-[9px] text-blue-400 font-semibold uppercase tracking-wider mt-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Opening{job.openings > 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <span className="flex items-center gap-1.5 text-xs text-slate-500" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <MapPin size={12} className="text-blue-400" /> {job.location}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-slate-400" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <Clock size={12} className="text-slate-300" /> Posted {job.posted}
          </span>
        </div>

        {/* Skills */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {job.skills.map(s => (
            <span key={s} className="text-[10px] font-semibold px-2 py-0.5 bg-slate-50 border border-slate-200 text-slate-600 rounded-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {s}
            </span>
          ))}
        </div>

        {/* Description toggle */}
        <AnimatePresence>
          {expanded && (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="text-sm text-slate-500 leading-relaxed mb-4 overflow-hidden" style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {job.desc}
            </motion.p>
          )}
        </AnimatePresence>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs font-semibold text-blue-600 flex items-center gap-1 hover:text-blue-800 transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {expanded ? 'Show less' : 'View details'}
            <motion.span animate={{ rotate: expanded ? 90 : 0 }} transition={{ duration: 0.2 }}>
              <ChevronRight size={13} />
            </motion.span>
          </button>

          {/* Premium button with shine effect and hero overlay style */}
          <button
            onClick={() => onApply(job)}
            className="relative overflow-hidden flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-white/20 before:to-transparent before:transition-transform before:duration-500 hover:before:translate-x-0"
          >
            Apply Now <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Main Page ──────────────────────────────────────────── */
export default function Careers() {
  const [keyword, setKeyword] = useState('');
  const [typeFilter, setType] = useState('All');
  const [locFilter, setLoc] = useState('All');
  const [applyJob, setApplyJob] = useState(null);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);

  const filtered = JOBS.filter(j => {
    const kw = keyword.toLowerCase();
    const matchKw = !kw || j.title.toLowerCase().includes(kw) || j.dept.toLowerCase().includes(kw) || j.location.toLowerCase().includes(kw);
    const matchType = typeFilter === 'All' || j.type === typeFilter;
    const matchLoc = locFilter === 'All' || j.location.includes(locFilter);
    return matchKw && matchType && matchLoc;
  });

  const clearFilters = () => {
    setKeyword('');
    setType('All');
    setLoc('All');
  };
  const hasFilters = keyword || typeFilter !== 'All' || locFilter !== 'All';

  return (
    <>
      <FontLink />
      <Navbar />

      {/* Hero Section - Premium overlay and effects */}
      <section ref={heroRef} className="relative overflow-hidden" style={{ minHeight: 'clamp(580px, 85vh, 820px)' }}>
        {/* Parallax background image */}
        <motion.div className="absolute inset-0" style={{ y: imgY }}>
          <img
            src="https://images.unsplash.com/photo-1582719471384-894fbb16e074?auto=format&fit=crop&w=1920&q=80"
            alt="Laboratory instruments"
            className="w-full h-full object-cover object-center scale-110"
          />
          {/* Hero Image overlay - brighter and more modern */}
          <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(15,35,86,0.88)_0%,rgba(30,58,138,0.72)_50%,rgba(14,165,233,0.82)_100%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_top,#0f2356_0%,transparent_55%)]" />
        </motion.div>

        {/* Grid pattern - Tailwind only */}
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '64px 64px'
        }} />

        {/* Blue radial accent */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] pointer-events-none bg-gradient-to-bl from-sky-500/10 to-transparent rounded-full blur-3xl" />

        {/* Content */}
        <div className="relative z-10 max-w-8xl mx-auto px-4 sm:px-8 lg:px-20 flex flex-col justify-center h-full"
          style={{ paddingTop: 'clamp(80px,12vw,140px)', paddingBottom: 'clamp(60px,10vw,120px)' }}>
          
          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-3 mb-6"
          >
            <div className="w-8 h-px bg-sky-400" />
            <span className="text-[11px] font-semibold tracking-[0.3em] uppercase text-sky-300" style={{ fontFamily: "'Outfit', sans-serif" }}>
              SmartLabTech · Careers
            </span>
          </motion.div>

          {/* Headline - Premium gradient text */}
          <motion.h1
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-7xl xl:text-8xl font-bold text-white leading-[0.92] mb-6"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.02em' }}
          >
            Build Your Career<br />
            <span className="bg-gradient-to-r from-sky-300 via-blue-300 to-sky-300 bg-clip-text text-transparent">With Purpose.</span>
          </motion.h1>

          {/* Subtext */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.22 }}
            className="text-base sm:text-lg leading-relaxed mb-10 max-w-lg text-white/80" style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Join SmartLabTech — helping world-class scientific instruments reach research labs, pharma companies, and universities across India.
          </motion.p>

          {/* CTA row - Premium buttons with shine effect and hero overlay style */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.34 }}
            className="flex flex-wrap gap-3"
          >
            <a
              href="#listings"
              className="relative overflow-hidden inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-bold bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)] text-white hover:shadow-lg hover:-translate-y-0.5 transition-all before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-white/20 before:to-transparent before:transition-transform before:duration-500 hover:before:translate-x-0"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              View Open Roles <ArrowRight size={15} />
            </a>
            <a
              href="mailto:careers@smartlabtech.in"
              className="relative overflow-hidden inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold bg-white/10 border border-white/20 text-white hover:bg-white/20 hover:-translate-y-0.5 transition-all before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-white/20 before:to-transparent before:transition-transform before:duration-500 hover:before:translate-x-0"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              <Mail size={14} /> Send Your CV
            </a>
          </motion.div>
        </div>
      </section>

    
      {/* Listings Section */}
      <section id="listings" className="bg-slate-50 py-16">
        <div className="max-w-8xl mx-auto px-4 sm:px-8 lg:px-20">
          
          {/* Section header */}
          <Reveal className="mb-10">
            <div className="flex items-end justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-px bg-blue-600" />
                  <span className="text-[10px] font-semibold tracking-[0.25em] uppercase text-blue-600" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Job Archives
                  </span>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Open <span className="bg-gradient-to-r from-blue-700 to-sky-500 bg-clip-text text-transparent">Positions</span>
                </h2>
              </div>
              <p className="text-sm text-slate-500" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {filtered.length} position{filtered.length !== 1 ? 's' : ''} found
              </p>
            </div>
          </Reveal>

          <div className="flex flex-col lg:flex-row gap-8 items-start">
            
            {/* Sidebar Filters */}
            <Reveal delay={0.1} className="w-full lg:w-64 flex-shrink-0 lg:sticky lg:top-28">
              <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <Filter size={13} className="text-blue-700" />
                    <span className="text-sm font-semibold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Filters</span>
                  </div>
                  {hasFilters && (
                    <button
                      onClick={clearFilters}
                      className="flex items-center gap-1 text-[11px] font-semibold text-red-400 hover:text-red-600 transition-colors"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      <X size={10} /> Clear all
                    </button>
                  )}
                </div>

                {/* Keyword */}
                <div className="mb-5">
                  <p className="text-[10px] font-semibold uppercase tracking-widest mb-2 text-slate-400" style={{ fontFamily: "'Outfit', sans-serif" }}>Keywords</p>
                  <div className="relative">
                    <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Title, skill…"
                      value={keyword}
                      onChange={e => setKeyword(e.target.value)}
                      className="w-full pl-8 pr-3 py-2.5 text-sm border-2 border-slate-200 rounded-xl bg-slate-50 outline-none focus:border-blue-500 focus:bg-white transition-all"
                    />
                  </div>
                </div>

                {/* Job Type - Premium gradient active state */}
                <div className="mb-5">
                  <p className="text-[10px] font-semibold uppercase tracking-widest mb-2.5 text-slate-400" style={{ fontFamily: "'Outfit', sans-serif" }}>Job Type</p>
                  <div className="flex flex-col gap-1">
                    {JOB_TYPES.map(t => (
                      <button
                        key={t}
                        onClick={() => setType(t)}
                        className={`w-full text-left px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                          typeFilter === t
                            ? 'text-white bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)]'
                            : 'text-slate-500 hover:bg-slate-50 hover:text-blue-700'
                        }`}
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                      >
                        {t === 'All' ? 'All Types' : t}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Location - Premium gradient active state */}
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest mb-2.5 text-slate-400" style={{ fontFamily: "'Outfit', sans-serif" }}>Location</p>
                  <div className="flex flex-col gap-1">
                    {LOCATIONS.map(l => (
                      <button
                        key={l}
                        onClick={() => setLoc(l)}
                        className={`w-full text-left px-3 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 ${
                          locFilter === l
                            ? 'text-white bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)]'
                            : 'text-slate-500 hover:bg-slate-50 hover:text-blue-700'
                        }`}
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                      >
                        {l !== 'All' && <MapPin size={10} />}
                        {l === 'All' ? 'All Locations' : l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Job List */}
            <div className="flex-1 min-w-0">
              {/* Active chips */}
              {hasFilters && (
                <div className="flex flex-wrap gap-2 mb-5">
                  {keyword && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1 rounded-full">
                      "{keyword}"
                      <button onClick={() => setKeyword('')} className="text-blue-400 hover:text-blue-700">
                        <X size={11} />
                      </button>
                    </span>
                  )}
                  {typeFilter !== 'All' && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 bg-sky-50 border border-sky-200 px-3 py-1 rounded-full">
                      {typeFilter}
                      <button onClick={() => setType('All')} className="text-sky-400 hover:text-sky-700">
                        <X size={11} />
                      </button>
                    </span>
                  )}
                  {locFilter !== 'All' && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-100 border border-slate-200 px-3 py-1 rounded-full">
                      <MapPin size={10} /> {locFilter}
                      <button onClick={() => setLoc('All')} className="text-slate-400 hover:text-slate-700">
                        <X size={11} />
                      </button>
                    </span>
                  )}
                </div>
              )}

              {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl border border-slate-100">
                  <p className="text-4xl mb-4">🔍</p>
                  <p className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>No positions found</p>
                  <p className="text-sm text-slate-400 mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>Try adjusting your filters or keywords</p>
                  <button
                    onClick={clearFilters}
                    className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)] hover:shadow-lg transition-all"
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {filtered.map((job, i) => (
                    <JobCard key={job.id} job={job} index={i} onApply={setApplyJob} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner - Premium gradient with hero overlay style */}
      <Reveal>
        <div className="py-16 px-4 bg-slate-50">
          <div className="max-w-8xl mx-auto px-4 sm:px-8 lg:px-20">
            <div className="rounded-2xl p-8 sm:p-12 flex flex-wrap items-center justify-between gap-6 bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)] shadow-xl">
              <div>
                <p className="text-[11px] font-bold text-sky-200 tracking-widest uppercase mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Can't find your role?</p>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Send us your Resume</h3>
                <p className="text-sm text-white/70 max-w-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  We're always looking for talented people. Drop your CV and we'll keep it on file for future openings.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="mailto:careers@smartlabtech.in"
                  className="relative overflow-hidden inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-bold text-white bg-[linear-gradient(135deg,rgba(15,35,86,0.95)_0%,rgba(30,58,138,0.9)_50%,rgba(14,165,233,0.95)_100%)] hover:shadow-lg hover:-translate-y-0.5 transition-all before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-white/20 before:to-transparent before:transition-transform before:duration-500 hover:before:translate-x-0"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  <Mail size={15} /> Email Your CV
                </a>
                <a
                  href="tel:+919876543210"
                  className="relative overflow-hidden inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-bold text-white border-2 border-white/30 hover:bg-white/10 hover:-translate-y-0.5 transition-all before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-white/20 before:to-transparent before:transition-transform before:duration-500 hover:before:translate-x-0"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  <Phone size={15} /> Call HR Team
                </a>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      <Footer />

      {/* Apply Modal */}
      <AnimatePresence>
        {applyJob && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setApplyJob(null)}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[2000]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 28 }}
              onClick={e => e.stopPropagation()}
              className="fixed inset-0 flex items-center justify-center p-4 sm:p-6 z-[2001]"
            >
              <div className="w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl">
                <ApplyModal job={applyJob} onClose={() => setApplyJob(null)} />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}